import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useClient } from "@/contexts/ClientContext";

interface RewardsMarqueeProps {
  /** Hide the "Joue et gagne" caption when the page already says it. */
  showLabel?: boolean;
  className?: string;
}

/**
 * RewardsMarquee — the prizes on offer, scrolling like a ticker. This is the
 * venue's own content (emoji + label per reward), so it is the one place where
 * emojis are the point.
 */
const RewardsMarquee = ({ showLabel = true, className = "" }: RewardsMarqueeProps) => {
  const { client } = useClient();

  const { data: rewards } = useQuery({
    queryKey: ["rewards-marquee", client?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rewards")
        .select("emoji, description")
        .eq("client_id", client!.id)
        .eq("is_active", true);
      if (error) throw error;
      return data;
    },
    enabled: !!client?.id,
  });

  if (!rewards || rewards.length === 0) return null;

  const separator = <span className="text-primary-ink/40" aria-hidden="true">·</span>;

  const content = rewards.flatMap((r, i) => [
    <span key={`item-${i}`} className="inline-flex items-center gap-2 px-3 whitespace-nowrap">
      <span aria-hidden="true">{r.emoji}</span>
      <span>{r.description}</span>
    </span>,
    ...(i < rewards.length - 1
      ? [<span key={`sep-${i}`} className="text-primary-ink/40" aria-hidden="true">·</span>]
      : []),
  ]);

  return (
    <div className={`w-full space-y-1.5 ${className}`}>
      {showLabel && (
        <p className="font-display text-center text-sm font-bold text-primary-ink">
          ✨ Joue et gagne ✨
        </p>
      )}
      <div className="w-full overflow-hidden rounded-2xl border border-primary/15 bg-primary/[0.07] py-2.5">
        <div className="inline-flex animate-marquee text-[15px] font-medium text-foreground">
          <div className="flex items-center">
            {content}
            {separator}
          </div>
          <div className="flex items-center" aria-hidden="true">
            {content}
            {separator}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardsMarquee;
